import React, { useMemo } from "react";
import PropTypes from "prop-types";
import styles from "./FeedConfigBar.module.css";

import PublishStatusIcon from "../PublishStatusIcon";
import SignalBadge from "../SignalBadge";
import PinLevelBadge from "../PinLevelPermissionBadge";
import PlatformIcon from "../PlatformIcon";

/**
 * View component.
 * A status bar that displays a feed's current configurations.
 */
export function FeedConfigBar({
  publishStatus,
  date,
  unpublishDate,
  signal,
  pinLevelPermissions,
  platforms,
}) {
  
  const pinOrder = ["AssociateIbo", "Platinum", "Emerald", "Diamond"];
  const platformOrder = ["Web", "iOS", "Android"];

  // Format date into mm/dd/yyy
  // const formattedDate = useMemo(
  //   () =>
  //     new Date(date).toLocaleDateString("en-US", {
  //       month: "2-digit",
  //       day: "2-digit",
  //       year: "numeric",
  //     }),
  //   [date]
  // );

  // Add validity check to date formatting so we don't always get the invalid date message
  const formattedDate = useMemo(() => {
    // console.log("FeedConfigBar received date prop:", date);
    const parsedDate = new Date(date + "T00:00:00");
    // console.log("Parsed date object", parsedDate);

    return !isNaN(parsedDate)
      ? parsedDate.toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        })
      : "Invalid Date";
  }, [date]);

  const formattedUnPublishedDate = useMemo(() => {
    // console.log("FeedConfigBar received date prop:", unpublishDate);
    const parsedDate = new Date(unpublishDate + "T00:00:00");
    // console.log("Parsed date object", parsedDate);

    return !isNaN(parsedDate)
      ? parsedDate.toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        })
      : "Invalid Date";
  }, [unpublishDate]);


  return (
    <div className={styles.container}>
      {publishStatus !== "Storaged" && (
        <div className={styles.status}>
          <PublishStatusIcon status={publishStatus} />
        </div>
      )}
      <p>
        {formattedDate}{" "}
        {formattedUnPublishedDate !== "Invalid Date" &&
          ` - ${formattedUnPublishedDate}`}
      </p>
      <div className={styles.signal}>
        <SignalBadge signal={signal} />
      </div>
      <ul className={styles["badge-list"]}>
        {pinLevelPermissions
          .sort((a, b) => {
            const indexA = pinOrder.indexOf(a);
            const indexB = pinOrder.indexOf(b);
            return indexA > indexB ? 1 : indexA < indexB ? -1 : 0;
          })
          // Update mapping to ensure unique key integrity (no weird dupes (e.g., 'DIA'))
          .map((permission, index) => (
            <li key={`${permission}-${index}`}>
              <PinLevelBadge pinLevelType={permission} />
            </li>
          ))}
      </ul>
      <ul className={styles["platform-list"]}>
        {platforms
          .sort((a, b) => {
            const indexA = platformOrder.indexOf(a);
            const indexB = platformOrder.indexOf(b);
            return indexA > indexB ? 1 : indexA < indexB ? -1 : 0;
          })
          .map((platform) => (
            <li key={platform} className={styles.platform}>
              <PlatformIcon platformType={platform} />
            </li>
          ))}
      </ul>
    </div>
  );
}

FeedConfigBar.propTypes = {
  /**
   * String that determines the status type of the icon.
   */
  publishStatus: PropTypes.oneOf([
    "Published",
    "Unpublished",
    "Archived",
    "Storaged",
  ]),
  /**
   * M/DD/YYYY of the feed item.
   */
  date: PropTypes.string,
  unpublishDate: PropTypes.string,
  /**
   * Number that indicates the signal level.
   */
  signal: PropTypes.number,
  /**
   * Array that contains the current pin level permission names.
   */
  pinLevelPermissions: PropTypes.arrayOf(
    PropTypes.oneOf(["AssociateIbo", "Platinum", "Emerald", "Diamond"])
  ),
  /**
   * Array of compatible platforms.
   */
  platforms: PropTypes.arrayOf(PropTypes.oneOf(["Web", "iOS", "Android"])),
};

FeedConfigBar.defaultProps = {
  publishStatus: "Storaged",
  pinLevelPermissions: [],
  platforms: [],
};
